import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  FormGroup,
  Card,
  CardBody,
  Label,
} from "reactstrap";

import TextareaAutosize from "react-textarea-autosize";

import { withTranslation } from "react-i18next";

import {
  selectStyle,
  getOptions,
  setClassName,
  renderButtons,
} from "../../Libs/CrudUtils/crudUtils";

import { showToast } from "../../Libs/CustomMethods/showToast";

import Breadcrumbs from "../../components/Common/Breadcrumb";

import { useForm } from "react-hook-form";

import { yupResolver } from "@hookform/resolvers/yup";

import { schemaShow } from "./SurveyValidation";

import Select from "react-select";

import {
  getDataShow,
  setDataShow,
  dataCreate,
  dataEdit,
  setDataIndex,
  apiReset,
} from "../../store/surveys/actions";
import { isArray } from "../../Libs/CustomMethods/customMethods";
import { isEmpty } from "lodash";

let validFields = {};

const NewSurvey = ({ t }) => {
  let { idData } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const { register, handleSubmit, setValue, errors } = useForm({
    resolver: yupResolver(schemaShow),
  });

  const [dataValue, setDataValue] = useState(null);
  const [dataValueBk, setDataValueBk] = useState(null);
  const [enableSubmit, setEnableSubmit] = useState(false);

  // #BEGIN - Editable variables

  const mainPath = "/surveys";
  const mainTitle = t("Surveys");
  const path = "/survey";
  const pathTitle = t("Survey");

  const {
    dataShow,
    dataForm,
    loadingDataShow,
    loadingDataCreate,
    loadingDataEdit,
    success,
    error,
  } = useSelector((state) => state.Survey);

  const responseToYup = (data) => {
    let objParsed = {
      name: data?.name,
      type: data?.type,
      info: data?.info,
    };
    return objParsed;
  };

  const yupToRequest = (data) => {
    let objParsed = {
      name: data?.name,
      type: data?.type,
      info: JSON.stringify(data?.info),
    };
    return objParsed;
  };

  const renderInfoError = (error) => {
    if (error) {
      const arrayMsgErrorDots = JSON.stringify(error).match(
        /(?<="message":").+?(?=")/gim
      );
      const arrayMsgError = arrayMsgErrorDots.map((e) => {
        const msg = e.split(".").pop();
        return msg;
      });
      const map = new Map();
      const messages = [];
      arrayMsgError.forEach((item) => {
        if (!map.has(item)) {
          map.set(item, true);
          messages.push(item);
        }
      });
      return messages.map((message) => (
        <div className="invalid-feedback d-block">
          {t(message?.split(" ")[0]) + " "}
          {message?.includes("is a required field") ? t("is required") : ""}
          {message?.includes("invalid type") ? t("invalid type") : ""}
        </div>
      ));
    }
  };

  const renderInputs = () => (
    <Row className="mb-3">
      <Col lg="6">
        <Row>
          <Col xs="12">
            <FormGroup>
              <Label>{t("Name") + " *"}</Label>
              <input
                ref={() => register({ name: "name" })}
                type="text"
                className={setClassName(errors.name, "form-control")}
                placeholder={t("Enter name")}
                onChange={(e) => {
                  let value = e?.target?.value;
                  updateValue("name", value, dataValueBk);
                }}
                value={dataValue?.name ?? ""}
              />
              <div className="invalid-feedback d-block">
                {t(errors.name?.message)}
              </div>
            </FormGroup>
          </Col>
          <Col xs="12">
            <FormGroup className="select2-container">
              <Label>{t("Type") + " *"}</Label>
              <Select
                ref={() => register({ name: "type" })}
                classNamePrefix="select2-selection"
                className={setClassName(errors.type, " select2-selection")}
                styles={selectStyle}
                placeholder={t("Choose") + "..."}
                options={getOptions(
                  dataForm?.types.map((e) => ({ id: e, name: e })),
                  loadingDataShow
                )}
                onChange={(e) => {
                  let value = e?.value;
                  updateValue("type", value, dataValueBk);
                }}
                value={
                  getOptions(
                    dataForm?.types.map((e) => ({ id: e, name: e })),
                    loadingDataShow
                  )?.find((e) => e.value === dataValue?.type) ?? null
                }
              />

              <div className="invalid-feedback d-block">
                {t(errors.type?.message)}
              </div>
            </FormGroup>
          </Col>
          <Col xs="12">
            <FormGroup>
              <Label>{t("Info") + " *"}</Label>
              <TextareaAutosize
                ref={() => register({ name: "info" })}
                type="textarea"
                className={setClassName(errors.info, "form-control")}
                placeholder={t("Enter info")}
                onChange={(e) => {
                  let value = e?.target?.value;
                  updateValue("info", value, dataValueBk);
                }}
                value={dataValue?.info ?? ""}
                minRows="6"
              />
              {renderInfoError(errors?.info)}
            </FormGroup>
          </Col>
        </Row>
      </Col>
    </Row>
  );

  // #END - Editable variables

  const handleCreateEdit = (data) => {
    let obj = yupToRequest(data);
    if (idData) {
      dispatch(dataEdit(idData, obj));
    } else {
      dispatch(dataCreate(obj));
    }
  };

  const updateEnableSubmit = (target, value, data) => {
    let { [target]: tg, ...rest } = validFields;

    let sortedData = isArray(value) ? value.sort() : value;
    let sortedDataBk = isArray(data?.[target])
      ? data?.[target].sort()
      : data?.[target];

    let isEqual = JSON.stringify(sortedDataBk) === JSON.stringify(sortedData);

    validFields = isEqual ? { ...rest } : { ...validFields, [target]: value };

    let enable = Object.keys(validFields)?.length > 0;
    setEnableSubmit(enable);
  };

  const updateValue = useCallback(
    (target, value, data) => {
      updateEnableSubmit(target, value, data);
      setDataValue((prev) => ({
        ...prev,
        [target]: value,
      }));
    },
    [setValue]
  );

  const updateValueBk = useCallback((data) => {
    let obj = responseToYup(data);
    setDataValueBk(obj);
  }, []);

  const onToastHidden = useCallback(() => {
    dispatch(apiReset());
    if (success) {
      dispatch(setDataShow({ show: null, form: dataForm }));
      dispatch(setDataIndex({ index: null, form: dataForm }));
      history.push(mainPath);
    }
  }, [dispatch, history, success, dataForm]);

  useEffect(() => {
    dataValueBk &&
      Object.entries(dataValueBk).forEach((item) =>
        updateValue(item[0], item[1], dataValueBk)
      );
  }, [dataValueBk, updateValue]);

  useEffect(() => {
    if (dataValue) {
      Object.entries(dataValue).forEach((item) =>
        setValue(item[0], item[1], {
          shouldValidate: item[1],
        })
      );
    }
  }, [dataValue, setValue]);

  useEffect(() => {
    updateValueBk(dataShow);
  }, [dataShow, updateValueBk]);

  useEffect(() => {
    if (idData) {
      dataShow?.id !== idData && dispatch(getDataShow(idData, true));
    } else {
      dataForm
        ? dispatch(setDataShow({ show: null, form: dataForm }))
        : dispatch(getDataShow(null, true));
    }
  }, [dataForm, dataShow?.id, dispatch, idData]);

  useEffect(() => {
    success && showToast(t(success), "success", onToastHidden);
  }, [success, onToastHidden, t]);
  useEffect(() => {
    if (error) {
      if (isArray(error)) {
        error.forEach((element) =>
          showToast(t(element.value), "error", onToastHidden)
        );
      } else {
        showToast(t(error), "error", onToastHidden);
      }
    }
  }, [error, onToastHidden, t]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            mainPath={mainPath}
            path={path}
            title={mainTitle}
            breadcrumbItem={
              idData
                ? t("Edit") + " " + pathTitle
                : t("Create") + " " + pathTitle
            }
          />
          <Row>
            <Col xs="12" className="customContent">
              <Card>
                <CardBody>
                  <form onSubmit={handleSubmit(handleCreateEdit)}>
                    {renderInputs()}
                    {renderButtons(
                      enableSubmit,
                      loadingDataCreate || loadingDataEdit,
                      mainPath,
                      null,
                      t
                    )}
                  </form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withTranslation()(NewSurvey);
